import { useEffect, useState, useCallback, useMemo } from "react";
import { X, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Loader2, Send, Calculator, Info, RotateCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * EstimateSimulator — modal for the Gestor to ajustar/simular/validar
 * the estimate margin before sending a proposal.
 *
 * Modes (radio at top of modal):
 *   * "cliente" — operator edits the client monthly revenue; AF cost stays
 *   * "afs"    — operator edits the AF monthly cost; revenue stays
 *
 * The backend endpoint is the same either way (it accepts both fields);
 * modes are purely a UX aid so the operator focuses on one lever at a time.
 *
 * When margin < tier threshold:
 *   * Red banner with the threshold + category label
 *   * "Pedir autorização especial" opens a reason textarea + button
 * When margin ≥ threshold:
 *   * Green banner + "Validar simulação" button (persists on the estimate)
 */

const EUR = (v) => new Intl.NumberFormat("pt-PT", {
  style: "currency", currency: "EUR",
}).format(Number(v || 0));

export default function EstimateSimulator({ open, onClose, lead, onValidated }) {
  const est = lead?.estimate || {};
  const primaryOption = useMemo(() => {
    const opts = est.options || [];
    if (est.primary_option_id) {
      const m = opts.find(o => o.id === est.primary_option_id);
      if (m) return m;
    }
    return opts.find(o => o.is_primary) || opts[0] || null;
  }, [est]);

  const initialRevenue = useMemo(() => {
    if (!primaryOption) return 0;
    const mode = (primaryOption.mode || est.continuity_type || "continuidade").toLowerCase();
    return Number((mode === "pontual" ? primaryOption.total_value : primaryOption.monthly_avg) || 0);
  }, [primaryOption, est.continuity_type]);

  const [mode, setMode] = useState("afs");
  const [afCost, setAfCost] = useState(() => Number(est.af_monthly_cost || 0));
  const [revenue, setRevenue] = useState(initialRevenue);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [authRequest, setAuthRequest] = useState(() =>
    est.margin_authorization_id ? { id: est.margin_authorization_id } : null
  );

  // Reset when modal opens with a different lead.
  useEffect(() => {
    if (!open) return;
    setAfCost(Number(est.af_monthly_cost || 0));
    setRevenue(initialRevenue);
    setResult(null);
    setReason("");
    setShowAuthForm(false);
  }, [open, est.af_monthly_cost, initialRevenue]);

  const simulate = useCallback(async () => {
    if (!lead?.id) return;
    setBusy(true);
    try {
      const body = { monthly_af_cost: Number(afCost) || 0 };
      if (mode === "cliente") body.monthly_revenue = Number(revenue) || 0;
      const r = await api.post(`/leads/${lead.id}/estimate/simulate-margin`, body);
      setResult(r.data);
      if (mode === "afs" && r.data?.monthly_revenue) {
        setRevenue(r.data.monthly_revenue);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a simular margem.");
    } finally { setBusy(false); }
  }, [lead?.id, afCost, revenue, mode]);

  // Auto-simulate when inputs change (debounced 400ms).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(simulate, 400);
    return () => clearTimeout(t);
  }, [open, simulate]);

  const validate = async () => {
    setBusy(true);
    try {
      const body = { monthly_af_cost: Number(afCost) || 0 };
      if (mode === "cliente") body.monthly_revenue = Number(revenue) || 0;
      if (authRequest?.id) body.margin_authorization_id = authRequest.id;
      await api.post(`/leads/${lead.id}/estimate/validate-margin`, body);
      toast.success("Simulação validada — tecto de custo AFs fixado na estimativa.");
      onValidated?.();
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Erro a validar simulação.");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const requestAuthorization = async () => {
    if (!reason.trim()) {
      toast.error("Motivo obrigatório para pedir autorização.");
      return;
    }
    setBusy(true);
    try {
      const body = { monthly_af_cost: Number(afCost) || 0, reason: reason.trim() };
      if (mode === "cliente") body.monthly_revenue = Number(revenue) || 0;
      const r = await api.post(
        `/leads/${lead.id}/estimate/request-margin-authorization`, body,
      );
      setAuthRequest(r.data);
      setShowAuthForm(false);
      toast.success("Pedido enviado ao admin. Aguarda decisão em 48h.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a submeter pedido.");
    } finally { setBusy(false); }
  };

  const resendAuthorization = async () => {
    if (!authRequest?.id) return;
    setBusy(true);
    try {
      const r = await api.post(`/margin-authorizations/${authRequest.id}/resend`);
      setAuthRequest((prev) => ({
        ...(prev || {}),
        last_resent_at: r.data?.resent_at,
        resend_count: (prev?.resend_count || 0) + 1,
      }));
      toast.success("Notificação reenviada ao admin.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a reenviar notificação.");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  const canSubmit = result && result.threshold_ok;
  const needsAuth = result && result.requires_authorization;
  const marginTone = !result ? "" :
    result.threshold_ok ? "text-[hsl(var(--success))]" :
    result.margin_pct < 0 ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--warning))]";

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
         data-testid="estimate-simulator-modal">
      <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-2xl mt-12 mb-12">

        {/* Header */}
        <header className="flex items-start justify-between p-5 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-foreground text-background flex items-center justify-center shrink-0">
              <Calculator className="w-4 h-4" strokeWidth={1.6} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-heading font-semibold">
                Gestor · Ajustar · Simular · Validar
              </div>
              <h2 className="font-heading text-xl tracking-tight text-foreground">
                Simulador da Estimativa
              </h2>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                Testa cenários por AFs ou por Cliente. Se margem &lt; mínimo, pede autorização especial ao admin.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="estimate-simulator-close"
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors">
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Mode picker */}
        <div className="px-5 pt-4 flex items-center gap-2" data-testid="estimate-simulator-mode">
          {["afs", "cliente"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              data-testid={`sim-mode-${m}`}
              className={`h-8 px-3 rounded-md text-[12px] font-medium font-heading transition-all ${
                mode === m
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:bg-muted"
              }`}
            >
              {m === "afs" ? "Simular pelos AFs" : "Simular pelo Cliente"}
            </button>
          ))}
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-2 gap-4 p-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-heading font-semibold mb-1.5">
              Média mensal cliente
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              disabled={mode !== "cliente"}
              data-testid="sim-input-revenue"
              className={`w-full h-10 px-3 border rounded-md text-[14px] tabular-nums focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/25 focus:border-primary transition-shadow ${
                mode === "cliente" ? "bg-card border-border" : "bg-muted/40 border-border text-muted-foreground"
              }`}
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              {mode === "cliente" ? "editável" : "receita da opção primária"}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-heading font-semibold mb-1.5">
              Custo mensal AFs
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={afCost}
              onChange={(e) => setAfCost(e.target.value)}
              disabled={mode !== "afs"}
              data-testid="sim-input-af-cost"
              className={`w-full h-10 px-3 border rounded-md text-[14px] tabular-nums focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/25 focus:border-primary transition-shadow ${
                mode === "afs" ? "bg-card border-border" : "bg-muted/40 border-border text-muted-foreground"
              }`}
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              {mode === "afs" ? "€/mês agregado" : "input manual (o gestor sabe o €/h médio)"}
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="px-5 pb-4">
          {busy && !result ? (
            <div className="rounded-lg border border-border p-6 text-center text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> A simular…
            </div>
          ) : result ? (
            <div data-testid="sim-result-card"
                 className={`rounded-lg border p-5 ${
                   result.threshold_ok
                     ? "bg-[hsl(var(--success-bg))]/50 border-[hsl(var(--success))]/25"
                     : "bg-[hsl(var(--danger-bg))]/50 border-[hsl(var(--danger))]/25"
                 }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {result.threshold_ok
                    ? <CheckCircle2 className="w-5 h-5 text-[hsl(var(--success))]" strokeWidth={1.8} />
                    : <AlertTriangle className="w-5 h-5 text-[hsl(var(--danger))]" strokeWidth={1.8} />}
                  <span className={`font-heading font-semibold text-[15px] ${
                    result.threshold_ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"
                  }`}>
                    {result.threshold_ok
                      ? "Margem dentro do mínimo"
                      : "Margem abaixo do mínimo"}
                  </span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-heading font-semibold">
                  {result.service_category_label}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Receita</div>
                  <div className="text-[15px] font-heading tabular-nums text-foreground">{EUR(result.monthly_revenue)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Custo AF</div>
                  <div className="text-[15px] font-heading tabular-nums text-foreground">{EUR(result.monthly_af_cost)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Absoluto</div>
                  <div className={`text-[15px] font-heading tabular-nums ${marginTone}`}>
                    {EUR(result.monthly_absolute_margin)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Margem</div>
                  <div className={`text-[15px] font-heading tabular-nums font-bold ${marginTone}`}>
                    {result.margin_pct}%
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">mínimo {result.threshold_pct}%</div>
                </div>
              </div>
              {result.margin_pct < 0 && (
                <div className="flex items-start gap-2 text-[12px] text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))]/60 rounded p-2 mt-2">
                  <TrendingDown className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div>Margem negativa — o custo excede a receita. Impossível avançar.</div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-border border-dashed p-5 text-center text-muted-foreground text-sm">
              Introduz valores para simular.
            </div>
          )}
        </div>

        {/* Auth request state */}
        {authRequest?.id && (
          <div className="mx-5 mb-4 rounded-lg border border-[hsl(var(--info))]/25 bg-[hsl(var(--info-bg))]/50 border-l-4 border-l-blue-500 p-4"
               data-testid="sim-auth-status">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-[hsl(var(--info))] mt-0.5 shrink-0" strokeWidth={1.6} />
              <div className="flex-1 min-w-0 text-[13px] text-foreground">
                <div className="font-medium text-[hsl(var(--info))] mb-0.5">
                  Pedido de autorização especial · #{(authRequest.id || "").slice(0, 8)}
                </div>
                Estado: <span className="font-semibold">{authRequest.status || "pending"}</span>
                {authRequest.token && (
                  <> — decisão em <span className="tabular-nums">{authRequest.expires_at?.slice(0, 16).replace("T", " ") || "48h"}</span></>
                )}
                {authRequest.last_resent_at && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Última notificação reenviada em{" "}
                    <span className="tabular-nums">
                      {authRequest.last_resent_at.slice(0, 16).replace("T", " ")}
                    </span>
                    {authRequest.resend_count > 1 && ` · ${authRequest.resend_count}× no total`}
                  </div>
                )}
              </div>
              {(authRequest.status || "pending") === "pending" && (
                <button
                  type="button"
                  onClick={resendAuthorization}
                  disabled={busy}
                  data-testid="sim-auth-resend"
                  className="h-8 px-3 rounded-md text-[12px] font-medium border border-[hsl(var(--info))]/40 text-[hsl(var(--info))] hover:bg-[hsl(var(--info))]/10 disabled:opacity-60 flex items-center gap-1.5 font-heading whitespace-nowrap"
                  title="Reenvia o email de notificação ao admin"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                  Reenviar ao admin
                </button>
              )}
            </div>
          </div>
        )}

        {/* Auth reason form (visible when user clicks "Pedir autorização") */}
        {needsAuth && showAuthForm && !authRequest?.id && (
          <div className="mx-5 mb-4 rounded-lg border border-[hsl(var(--danger))]/25 bg-card p-4" data-testid="sim-auth-form">
            <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-heading font-semibold mb-1.5">
              Motivo do pedido de autorização (visível ao admin)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: cliente vinculado, fidelização longo-prazo, estratégico para portfolio de Braga…"
              rows={3}
              data-testid="sim-auth-reason"
              className="w-full px-3 py-2 border border-border rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/25 focus:border-primary transition-shadow resize-none"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => setShowAuthForm(false)}
                data-testid="sim-auth-cancel"
                className="h-9 px-3 rounded-md text-[13px] text-muted-foreground hover:bg-muted font-heading"
              >Cancelar</button>
              <button
                type="button"
                disabled={busy || !reason.trim()}
                onClick={requestAuthorization}
                data-testid="sim-auth-submit"
                className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-[13px] font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5 font-heading"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Enviar pedido
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <footer className="flex items-center justify-between p-5 border-t border-border bg-muted/40">
          <button type="button" onClick={onClose}
                  data-testid="sim-cancel"
                  className="h-9 px-4 rounded-md text-[13px] text-muted-foreground hover:bg-card hover:border hover:border-border font-heading">
            Fechar
          </button>
          <div className="flex items-center gap-2">
            {needsAuth && !showAuthForm && !authRequest?.id && (
              <button
                type="button"
                onClick={() => setShowAuthForm(true)}
                data-testid="sim-open-auth-form"
                className="h-9 px-4 rounded-md bg-red-600 text-primary-foreground text-[13px] font-medium hover:bg-red-700 flex items-center gap-1.5 font-heading"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> Pedir autorização
              </button>
            )}
            <button
              type="button"
              onClick={validate}
              disabled={busy || (!canSubmit && !authRequest?.id) || !result}
              data-testid="sim-validate"
              className="h-9 px-4 rounded-md bg-emerald-600 text-primary-foreground text-[13px] font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 font-heading"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
              Validar simulação
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
